/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { combineEpics } from "redux-observable";
import { EMPTY, from, merge, of, timer } from "rxjs";
import { ajax } from "rxjs/ajax";
import { catchError, exhaustMap, filter, map, mapTo, pluck, switchMap, tap, expand } from "rxjs/operators";
import { isActionOf } from "typesafe-actions";
import config from "../../config";
import { getDB } from "../../db/store";
import { USERS_STORE } from "../../db/stores/users";
import { createEpic, getCommonHeaders } from "../../utils/epics";
import { PagedResponse } from "../../utils/types";
import { CURRENT_USER } from './../../utils/types';
import { fetchUserDetails, fetchUsers, saveUsers, UserDescription } from "./actions";
import { fetchTokenActions } from '../session';
import * as Sentry from "@sentry/react";

//const BACKGROUND_SYNC_INTERVAL = 86400000; // 1 Day
const BACKGROUND_SYNC_INTERVAL = 3600000; // 1 hour
// const BACKGROUND_SYNC_INTERVAL = 60000; // 1 Minute

//const liasonRole = 'ROLE_EVALUATOR_LIAISON'

const fetchUserEpic = createEpic((action$, state$) =>
  action$.pipe(
    filter(isActionOf(fetchUserDetails.request)),
    exhaustMap(() => {
      if (state$.value.session.dfdmToken === null)
        return EMPTY;

      const headers = getCommonHeaders(state$);
      return ajax.get(`${config.dfdmHost}/api/users/${state$.value.session.dfdmToken!.sub}`, headers).pipe(
        expand(resp => {
          const response: PagedResponse<UserDescription> = resp.response;
          if (!response || !response.next) {
            return EMPTY;
          }
          return ajax.get(response.next, headers);
        }),
        pluck('response'),
        tap((user: UserDescription) => {
          localStorage.setItem(CURRENT_USER, JSON.stringify(user));
          Sentry.setUser({ id: user.id })
        }),
        map(user => fetchUserDetails.success(user)),
        catchError(e => of(fetchUserDetails.failure(e)))
      )
    })
  )
);

const scheduleUserFetchEpic = createEpic((action$) =>
  merge(
    timer(1000, BACKGROUND_SYNC_INTERVAL),
    action$.pipe(
      filter(isActionOf(fetchTokenActions.success))
    )
  ).pipe(
    switchMap(() => of(fetchUserDetails.request(), fetchUsers.request()))
  )
);

/**
 * Fetch users who the current user has permission to see.
 */
const fetchUsersEpic = createEpic((action$, state$) =>
  action$.pipe(
    filter(isActionOf(fetchUsers.request)),
    exhaustMap(() => {
      if (state$.value.session.dfdmToken === null) {
        return EMPTY;
      }
      return ajax.get(`${config.dfdmHost}/api/users?eval_client=true&certification_types[]=farm_ac_v5_evaluator&certification_types[]=farm_ac_v5_verifier&certification_types[]=farm_ac_v5_tester&max=1000`, getCommonHeaders(state$))
        .pipe(
          pluck('response'),
          switchMap((users: PagedResponse<UserDescription>) => of(saveUsers.request(users.results), fetchUsers.success(users.results))),
          catchError(e => of(fetchUsers.failure(e)))
        );
    }),
  ),
);

const saveUsersEpic = createEpic(action$ => {
  return action$.pipe(
    filter(isActionOf(saveUsers.request)),
    pluck('payload'),
    switchMap(users => {
      return from(getDB().then(db => {
        const store = db.transaction(USERS_STORE, 'readwrite').objectStore(USERS_STORE);
        for (
          const user of users
        ) {
          store.put(user);
        }
      })
      );
    }),
    mapTo(saveUsers.success())
  );
});



export default combineEpics(
  fetchUserEpic,
  scheduleUserFetchEpic,
  fetchUsersEpic,
  saveUsersEpic
)